import React, { createContext, useCallback, useMemo, useState } from 'react';

interface TableRow {
  id: string;
}

export type TableBulkActionsModeContextValue = {
  areAllVisibleSelected: boolean;
  areAnySelected: boolean;
  areAnyVisibleSelected: boolean;
  bulkRowDeleteLimit: number;
  getIsRowSelected: (row: TableRow) => boolean;
  isBulkActionsModeOn: boolean;
  isBulkDeleteLimitExceeded: boolean;
  onToggleAllVisibleRows: (isChecked: boolean) => void;
  onToggleRow: (row: TableRow, isChecked: boolean) => void;
  selectedCount: number;
  selectedIds: string[];
  setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>;
  setVisibleRows: (rows: TableRow[]) => void;
  turnBulkActionsModeOff: () => void;
  turnBulkActionsModeOn: () => void;
};

export const TableBulkActionsModeContext = createContext<
  TableBulkActionsModeContextValue | undefined
>(undefined);

type TableBulkActionsModeProviderProps = {
  bulkRowDeleteLimit: number;
  children: React.ReactNode;
};

export const TableBulkActionsModeProvider = ({
  bulkRowDeleteLimit,
  children,
}: TableBulkActionsModeProviderProps) => {
  const [isBulkActionsModeOn, setIsBulkActionsModeOn] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [visibleRows, setVisibleRows] = useState<TableRow[]>([]);

  const turnBulkActionsModeOn = useCallback(() => {
    setIsBulkActionsModeOn(true);
  }, []);
  const turnBulkActionsModeOff = useCallback(() => {
    setIsBulkActionsModeOn(false);
    setSelectedIds([]);
  }, []);

  const selectedCount = useMemo(() => selectedIds.length, [selectedIds]);
  const areAnySelected = useMemo(() => selectedCount > 0, [selectedCount]);
  const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const areAnyVisibleSelected = useMemo(
    () =>
      !!visibleRows.length && visibleRows.some((r) => selectedIdsSet.has(r.id)),
    [selectedIdsSet, visibleRows],
  );
  const areAllVisibleSelected = useMemo(
    () =>
      !!visibleRows.length &&
      visibleRows.every((r) => selectedIdsSet.has(r.id)),
    [selectedIdsSet, visibleRows],
  );
  const getIsRowSelected = useCallback(
    (row: TableRow) => selectedIdsSet.has(row.id),
    [selectedIdsSet],
  );
  const onToggleRow = useCallback(
    (row: TableRow, isChecked: boolean) => {
      setSelectedIds((prev) =>
        isChecked
          ? Array.from(new Set([...prev, row.id]))
          : prev.filter((id) => id !== row.id),
      );
    },
    [setSelectedIds],
  );
  const onToggleAllVisibleRows = useCallback(
    (isChecked: boolean) => {
      const visibleRowIdsSet = new Set(visibleRows.map(({ id }) => id));
      setSelectedIds((prev) =>
        isChecked
          ? Array.from(new Set([...prev, ...visibleRowIdsSet]))
          : prev.filter((id) => !visibleRowIdsSet.has(id)),
      );
    },
    [setSelectedIds, visibleRows],
  );

  const value = useMemo(
    () => ({
      areAllVisibleSelected,
      areAnySelected,
      areAnyVisibleSelected,
      getIsRowSelected,
      bulkRowDeleteLimit,
      isBulkActionsModeOn,
      isBulkDeleteLimitExceeded: selectedCount > bulkRowDeleteLimit,
      onToggleAllVisibleRows,
      onToggleRow,
      selectedCount,
      selectedIds,
      setSelectedIds,
      setVisibleRows,
      turnBulkActionsModeOff,
      turnBulkActionsModeOn,
    }),
    [
      areAllVisibleSelected,
      areAnySelected,
      areAnyVisibleSelected,
      bulkRowDeleteLimit,
      getIsRowSelected,
      isBulkActionsModeOn,
      onToggleAllVisibleRows,
      onToggleRow,
      selectedCount,
      selectedIds,
      setSelectedIds,
      setVisibleRows,
      turnBulkActionsModeOff,
      turnBulkActionsModeOn,
    ],
  );

  return (
    <TableBulkActionsModeContext.Provider value={value}>
      {children}
    </TableBulkActionsModeContext.Provider>
  );
};
